import React, { useState } from "react";
import { ChevronsUp, ChevronUp, ChevronDown, ChevronsDown, GripVertical, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/config";
import { BtnGhost, BtnDanger, Select } from "./AdminUI";

// Moves an item within its group (same featured flag + same visible filter), keeping other items in place.
const reorder = (all, visible, id, target) => {
  const item = all.find((x) => x.id === id);
  const group = visible.filter((x) => !!x.featured === !!item.featured);
  const from = group.findIndex((x) => x.id === id);
  const to = Math.max(0, Math.min(group.length - 1, target));
  if (from === to) return null;
  const moved = [...group];
  moved.splice(to, 0, moved.splice(from, 1)[0]);
  const groupIds = new Set(group.map((x) => x.id));
  let k = 0;
  return all.map((x) => (groupIds.has(x.id) ? moved[k++] : x));
};

const thumb = (url) => (url.includes("/api/files/") && !url.includes("?") ? `${url}?w=300` : url);

const MoveBtn = ({ icon: Icon, label, onClick, disabled, testid }) => (
  <button type="button" title={label} aria-label={label} onClick={onClick} disabled={disabled} data-testid={testid}
    className="p-1.5 border border-[#9A8640]/40 text-[#DDD4B2] hover:bg-[#9A8640] hover:text-[#171717] disabled:opacity-25 disabled:pointer-events-none transition-colors">
    <Icon size={14} />
  </button>
);

export const GallerySortableList = ({ items, setItems, setEditing, remove, cats }) => {
  const [cat, setCat] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const visible = cat === "all" ? items : items.filter((x) => x.category === cat);
  const labelOf = (slug) => cats.find((c) => c.slug === slug)?.label || slug;

  const apply = async (id, target) => {
    const next = reorder(items, visible, id, target);
    if (!next) return;
    const prev = items;
    setItems(next);
    try {
      await api.post("/admin/gallery/reorder", { ids: next.map((x) => x.id) });
      toast.success("Ordre enregistré");
    } catch {
      setItems(prev);
      toast.error("Erreur : ordre non enregistré");
    }
  };

  const onDrop = (target) => {
    const src = items.find((x) => x.id === dragId);
    setDragId(null); setOverId(null);
    if (!src || src.id === target.id) return;
    if (!!src.featured !== !!target.featured) {
      toast.info("Les réalisations phares restent toujours en tête");
      return;
    }
    const group = visible.filter((x) => !!x.featured === !!src.featured);
    apply(src.id, group.findIndex((x) => x.id === target.id));
  };

  return (
    <div data-testid="gallery-sortable">
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="w-64">
          <Select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="gallery-order-category-filter">
            <option value="all">{`Toutes les catégories (${items.length})`}</option>
            {cats.map((c) => <option key={c.slug} value={c.slug}>{`${c.label} (${items.filter((x) => x.category === c.slug).length})`}</option>)}
          </Select>
        </div>
        <p className="text-xs text-[#DDD4B2]/60 max-w-xl">
          Glissez-déposez les vignettes ou utilisez les flèches. L'ordre est enregistré automatiquement et identique sur la page Réalisations. Les réalisations phares (★) restent toujours en tête.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {visible.map((it) => {
          const group = visible.filter((x) => !!x.featured === !!it.featured);
          const gi = group.findIndex((x) => x.id === it.id);
          const pos = visible.indexOf(it) + 1;
          return (
            <div key={it.id} draggable
              onDragStart={(e) => { setDragId(it.id); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); if (overId !== it.id) setOverId(it.id); }}
              onDragLeave={() => setOverId(null)}
              onDrop={(e) => { e.preventDefault(); onDrop(it); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              className={`border bg-[#0f0f0f] flex flex-col transition-[opacity,border-color,transform] duration-150 ${dragId === it.id ? "opacity-40" : ""} ${overId === it.id && dragId !== it.id ? "border-[#9A8640] scale-[1.02]" : "border-[#9A8640]/25"}`}
              data-testid={`gallery-order-item-${it.id}`}>
              <div className="relative aspect-square overflow-hidden cursor-grab active:cursor-grabbing">
                {it.image_url && <img src={thumb(it.image_url)} alt="" loading="lazy" className="w-full h-full object-cover pointer-events-none" />}
                <span className="absolute top-2 left-2 bg-[#171717]/85 text-[#9A8640] text-xs px-2 py-0.5 font-medium" data-testid={`gallery-order-position-${it.id}`}>#{pos}</span>
                {it.featured && <span className="absolute top-2 right-2 bg-[#9A8640] text-[#171717] p-1"><Star size={12} /></span>}
                <span className="absolute bottom-2 right-2 bg-[#171717]/70 text-[#DDD4B2] p-1"><GripVertical size={14} /></span>
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <p className="text-[#DDD4B2] text-sm truncate" title={it.title}>{it.title}</p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#9A8640]/80 truncate">{labelOf(it.category)}</p>
                <div className="flex gap-1 mt-auto">
                  <MoveBtn icon={ChevronsUp} label="Premier" onClick={() => apply(it.id, 0)} disabled={gi === 0} testid={`gallery-move-first-${it.id}`} />
                  <MoveBtn icon={ChevronUp} label="Monter" onClick={() => apply(it.id, gi - 1)} disabled={gi === 0} testid={`gallery-move-up-${it.id}`} />
                  <MoveBtn icon={ChevronDown} label="Descendre" onClick={() => apply(it.id, gi + 1)} disabled={gi === group.length - 1} testid={`gallery-move-down-${it.id}`} />
                  <MoveBtn icon={ChevronsDown} label="Dernier" onClick={() => apply(it.id, group.length - 1)} disabled={gi === group.length - 1} testid={`gallery-move-last-${it.id}`} />
                </div>
                <div className="flex gap-1">
                  <BtnGhost className="!px-2 !py-1 flex-1 flex justify-center" onClick={() => setEditing(it)} data-testid={`gallery-edit-${it.id}`}><Pencil size={12} /></BtnGhost>
                  <BtnDanger className="!px-2 !py-1 flex-1 flex justify-center" onClick={() => remove(it.id)} data-testid={`gallery-delete-${it.id}`}><Trash2 size={12} /></BtnDanger>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {visible.length === 0 && <p className="text-[#DDD4B2]/60 text-sm">Aucune réalisation dans cette catégorie.</p>}
    </div>
  );
};
